import React, { useEffect, useState } from "react";
import { toast } from "./toast.js";
import { supabase } from "./supabaseClient.js";
import { useAuth } from "./auth.jsx";

const FEATS = [["accounting", "المحاسبة والمالية"], ["distribution", "التوزيع والتسويق"], ["exporting", "التصدير والتقارير"], ["activity_log", "سجل الدخول والمتابعة"]];

export default function PlanPicker({ onClose, currentPlan }) {
  const { me } = useAuth();
  const [plans, setPlans] = useState(null);
  const [yearly, setYearly] = useState(true);
  const [payInstr, setPayInstr] = useState("");
  const [pending, setPending] = useState(null);
  const [sentReq, setSentReq] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await supabase.from("plans").select("*").order("sort");
      setPlans((data || []).filter((p) => ["basic", "standard", "pro", "enterprise"].includes(p.id)));
      const { data: ps } = await supabase.from("platform_settings").select("value").eq("key", "pay_instructions").maybeSingle();
      if (ps) setPayInstr(ps.value || "");
      const { data: pr } = await supabase.from("profiles").select("org_id").eq("id", me.id).maybeSingle();
      if (pr) {
        const { data: rq } = await supabase.from("subscription_requests").select("*").eq("org_id", pr.org_id).eq("status", "pending").order("created_at", { ascending: false }).limit(1).maybeSingle();
        if (rq) setPending(rq);
      }
    } catch { setPlans([]); }
  })(); }, []);

  const ask = async (pl) => {
    setBusy(true);
    try {
      const { data: pr } = await supabase.from("profiles").select("org_id").eq("id", me.id).maybeSingle();
      const months = yearly ? 12 : 1;
      const { data, error } = await supabase.from("subscription_requests")
        .insert({ org_id: pr.org_id, requested_by: me.id, plan_id: pl.id, months }).select().maybeSingle();
      if (error) throw error;
      setSentReq({ ...data, plan_name: pl.name_ar });
    } catch (e) { try { toast("تعذر: " + (e.message || e), "err"); } catch {} }
    setBusy(false);
  };

  const confirmed = sentReq || pending;
  return (
    <div className="ag-modal-bg" onClick={onClose} style={{ zIndex: 1400 }}>
      <div className="ag-modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="ag-modal-h">
          <span>ترجمتك - الباقات والترقية</span>
          <button className="ag-btn sm" onClick={onClose}>إغلاق</button>
        </div>

        {confirmed ? (
          <div style={{ textAlign: "center", padding: "10px 6px 4px" }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: "var(--accent)" }}>استلمنا طلبكم ✓</div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--text)", marginTop: 8, lineHeight: 2 }}>
              باقة «{sentReq ? sentReq.plan_name : ""}» × {(confirmed.months === 12 ? "سنة كاملة" : "شهر")} - رقم الطلب: <span style={{ direction: "ltr", display: "inline-block", fontFamily: "monospace" }}>{String(confirmed.id).slice(0, 8)}</span>
            </div>
            <div style={{ margin: "16px auto 0", maxWidth: 520, textAlign: "right", fontSize: 13.5, lineHeight: 2.1, color: "var(--text)", borderRadius: 14, border: "1px dashed var(--accent)", padding: "13px 17px", background: "color-mix(in srgb, var(--accent) 5%, transparent)" }}>
              <b style={{ color: "var(--accent)" }}>لإتمام التفعيل:</b><br />{payInstr}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--faint)", marginTop: 12 }}>يصلكم إشعار فور التفعيل - وكل بياناتكم وأعمال فريقكم تستمر كما هي.</div>
          </div>
        ) : plans === null ? <div className="ag-empty">جار التحميل…</div> : (
          <>
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 16 }}>
              {[["شهري", false], ["سنوي - التزام سعر ثابت", true]].map(([lbl, v]) => (
                <button key={lbl} onClick={() => setYearly(v)} style={{ fontFamily: "inherit", fontSize: 13, fontWeight: 800, padding: "8px 20px", borderRadius: 99, cursor: "pointer", border: "1px solid " + (yearly === v ? "var(--accent)" : "var(--line)"), background: yearly === v ? "color-mix(in srgb, var(--accent) 10%, transparent)" : "transparent", color: yearly === v ? "var(--accent)" : "var(--text)" }}>{lbl}</button>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
              {plans.map((pl) => {
                const hot = pl.id === "standard";
                const cur = pl.id === currentPlan;
                return (
                  <div key={pl.id} style={{ position: "relative", borderRadius: 15, padding: "18px 15px",
                    border: hot ? "1.5px solid var(--accent)" : "1px solid var(--line)",
                    background: "color-mix(in srgb, var(--surface) 72%, transparent)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
                    {hot && <div style={{ position: "absolute", top: -10, right: 14, fontSize: 10.5, fontWeight: 800, color: "var(--bg)", background: "var(--accent)", borderRadius: 99, padding: "3px 11px" }}>الأكثر اختيارًا</div>}
                    <div style={{ fontSize: 16, fontWeight: 800, color: "var(--text)" }}>{pl.name_ar}{cur && <span style={{ fontSize: 10.5, color: "var(--accent)", marginRight: 6 }}>(باقتكم الحالية)</span>}</div>
                    <div style={{ margin: "8px 0 2px", fontSize: 25, fontWeight: 800, color: "var(--accent)" }}>
                      {pl.price_kwd != null ? <>{yearly ? (pl.price_kwd * 12).toLocaleString("en") : pl.price_kwd}<span style={{ fontSize: 11.5, color: "var(--faint)", fontWeight: 700 }}> د.ك/{yearly ? "سنة" : "شهر"}</span></> : <span style={{ fontSize: 16 }}>تعاقد سنوي</span>}
                    </div>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text)", borderTop: "1px dashed var(--line)", paddingTop: 9, marginTop: 7, lineHeight: 1.9 }}>
                      {pl.max_pms != null ? pl.max_pms + " مدير مشاريع" : "مدراء بلا حد"} · {pl.max_users != null ? pl.max_users + " عضو" : "أعضاء بلا حد"}
                    </div>
                    <div style={{ marginTop: 7 }}>
                      {FEATS.map(([k, l]) => {
                        const on = !!(pl.features && pl.features[k]);
                        return <div key={k} style={{ fontSize: 11.5, fontWeight: 700, padding: "2px 0", color: on ? "var(--text)" : "var(--faint)", opacity: on ? 1 : 0.55 }}><span style={{ color: on ? "var(--accent)" : "var(--faint)", fontWeight: 900 }}>{on ? "✓ " : "- "}</span>{l}</div>;
                      })}
                    </div>
                    <button disabled={busy} onClick={() => ask(pl)} style={{ marginTop: 12, width: "100%", fontFamily: "inherit", fontSize: 13, fontWeight: 800, padding: "9px 0", borderRadius: 11, cursor: "pointer",
                      border: hot ? "none" : "1px solid var(--accent)", background: hot ? "var(--accent)" : "transparent", color: hot ? "var(--bg)" : "var(--accent)", opacity: busy ? 0.6 : 1 }}>
                      أطلب التفعيل
                    </button>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: "var(--faint)", textAlign: "center", marginTop: 14, lineHeight: 1.9 }}>
              بلا بطاقة الآن - يصلكم ملخص الطلب وتعليمات الدفع، ويفعل حسابكم فور التأكيد.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
